import { motion, AnimatePresence } from 'framer-motion'
import { Icon } from '../common'
import './PromptSection.css'

/**
 * PromptSection - A collapsible section with toggle for the prompt builder
 * 
 * Props:
 * - title: Section title
 * - icon: Icon to display
 * - enabled: Whether this section is included in the output
 * - collapsed: Whether the section content is hidden
 * - onToggleEnabled: Callback when enabled toggle changes
 * - onToggleCollapsed: Callback when collapse toggle changes
 * - children: Section content
 */
export default function PromptSection({
  title,
  iconName = 'synopsis',
  enabled = true,
  collapsed = false,
  onToggleEnabled,
  onToggleCollapsed,
  children,
}) {
  const contentVariants = {
    collapsed: {
      height: 0,
      opacity: 0,
      transition: {
        height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
        opacity: { duration: 0.2 },
      },
    },
    expanded: {
      height: 'auto',
      opacity: 1,
      transition: {
        height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
        opacity: { duration: 0.3, delay: 0.1 },
      },
    },
  }

  return (
    <div className={`prompt-section ${enabled ? 'prompt-section--enabled' : 'prompt-section--disabled'}`}>
      <div className="prompt-section__header" onClick={onToggleCollapsed}>
        <div className="prompt-section__title-group">
          <span className="prompt-section__icon"><Icon name={iconName} size={16} /></span>
          <h3 className="prompt-section__title">{title}</h3>
        </div>
        
        <div className="prompt-section__controls">
          {/* Enable/Disable Toggle */}
          <button
            className={`prompt-section__toggle ${enabled ? 'prompt-section__toggle--on' : ''}`}
            onClick={(e) => {
              e.stopPropagation()
              onToggleEnabled?.()
            }}
            title={enabled ? 'Click to exclude from prompt' : 'Click to include in prompt'}
          >
            <span className="prompt-section__toggle-track">
              <span className="prompt-section__toggle-thumb" />
            </span>
            <span className="prompt-section__toggle-label">
              {enabled ? 'ON' : 'OFF'}
            </span>
          </button>
          
          {/* Collapse/Expand Arrow */}
          <button 
            className="prompt-section__collapse-btn"
            onClick={(e) => {
              e.stopPropagation()
              onToggleCollapsed?.()
            }}
            title={collapsed ? 'Expand section' : 'Collapse section'}
          >
            <motion.span
              className="prompt-section__arrow"
              animate={{ rotate: collapsed ? 0 : 180 }}
              transition={{ duration: 0.2 }}
            >
              <Icon name="chevron-down" size={16} />
            </motion.span>
          </button>
        </div>
      </div>
      
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            className="prompt-section__content-wrapper"
            variants={contentVariants}
            initial="collapsed"
            animate="expanded"
            exit="collapsed"
          >
            <div className={`prompt-section__content ${!enabled ? 'prompt-section__content--dimmed' : ''}`}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/**
 * PromptField - A single field within a prompt section
 */
export function PromptField({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  options = [],
  rows = 3,
  hint,
}) {
  const renderInput = () => {
    switch (type) {
      case 'textarea':
        return (
          <textarea
            className="prompt-field__textarea"
            value={value || ''}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={placeholder}
            rows={rows}
          />
        )
      
      case 'select':
        return (
          <select
            className="prompt-field__select"
            value={value || ''}
            onChange={(e) => onChange?.(e.target.value)}
          >
            {options.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.name} {opt.description && `— ${opt.description}`}
              </option>
            ))}
          </select>
        )
      
      case 'toggle':
        return (
          <button
            className={`prompt-field__toggle ${value ? 'prompt-field__toggle--on' : ''}`}
            onClick={() => onChange?.(!value)}
          >
            <span className="prompt-field__toggle-track">
              <span className="prompt-field__toggle-thumb" />
            </span>
          </button>
        )
      
      default:
        return (
          <input
            type="text"
            className="prompt-field__input"
            value={value || ''}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={placeholder}
          />
        )
    }
  }

  return (
    <div className={`prompt-field prompt-field--${type}`}>
      <label className="prompt-field__label">
        {label}
        {hint && <span className="prompt-field__hint">{hint}</span>}
      </label>
      {renderInput()}
    </div>
  )
}

/**
 * PromptFieldRow - Horizontal row of fields
 */
export function PromptFieldRow({ children }) {
  return <div className="prompt-field-row">{children}</div>
}

